<template>
  <v-form ref="form">
    <v-card outlined>
      <v-card-text v-if="requirement.pendingResponse">
        {{ $t("pendingResponse.title") }}
        <ResponseList :responses="[requirement.pendingResponse]" removable-files @remove-file="removeFile" />
      </v-card-text>

      <v-card-text>
        <MarkdownEditor
          :label="$t(`${type}.responseForm.response.label`)"
          :rules="[(v) => (!!v && v.trim().length > 0) || $t('validation.required')]"
          v-model="formData.text"
          @change="save"
        />

        <v-file-input
          multiple
          :label="$t(`${type}.responseForm.files.label`)"
          :hint="$t(`${type}.responseForm.files.hint`)"
          persistent-hint
          v-model="formData.files"
          small-chips
        ></v-file-input>
      </v-card-text>

      <v-card-actions>
        <v-btn color="primary" @click="save" :loading="loading">
          {{ $t(`${type}.responseForm.save`) }}
        </v-btn>

        <v-spacer />

        <v-btn v-if="requirement.pendingResponse" color="secondary" @click="remove" :loading="loading">
          {{ $t(`${type}.responseForm.remove`) }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="saved" color="secondary">
      <div class="text-center flex-grow-1">
        {{ $t("action.saved") }}
      </div>
    </v-snackbar>
  </v-form>
</template>

<script>
import ResponseList from "@/components/Fund/ResponseList";
import MarkdownEditor from "@/components/Common/MarkdownEditor.vue";
import UpdateUserResponseMinimalRequirementMutation from "@/gql/mutations/UpdateUserResponseMinimalRequirementMutation.gql";
import UpdateAuditorResponseMinimalRequirementMutation from "@/gql/mutations/UpdateAuditorResponseMinimalRequirementMutation.gql";
import UpdateUserResponseStepModelRequirementMutation from "@/gql/mutations/UpdateUserResponseStepModelRequirementMutation.gql";
import UpdateAuditorResponseStepModelRequirementMutation from "@/gql/mutations/UpdateAuditorResponseStepModelRequirementMutation.gql";

import RemoveUserPendingResponseMinimalRequirementMutation from "@/gql/mutations/RemoveUserPendingResponseMinimalRequirementMutation.gql";
import RemoveAuditorPendingResponseMinimalRequirementMutation from "@/gql/mutations/RemoveAuditorPendingResponseMinimalRequirementMutation.gql";
import RemoveAuditorPendingResponseStepModelRequirementMutation from "@/gql/mutations/RemoveAuditorPendingResponseStepModelRequirementMutation.gql";
import RemoveUserPendingResponseStepModelRequirementMutation from "@/gql/mutations/RemoveUserPendingResponseStepModelRequirementMutation.gql";

import UserRemoveFilePendingResponseMinimalRequirementMutation from "@/gql/mutations/UserRemoveFilePendingResponseMinimalRequirementMutation.gql";
import UserRemoveFilePendingResponseStepModelRequirementMutation from "@/gql/mutations/UserRemoveFilePendingResponseStepModelRequirementMutation.gql";
import AuditorRemoveFilePendingResponseMinimalRequirementMutation from "@/gql/mutations/AuditorRemoveFilePendingResponseMinimalRequirementMutation.gql";
import AuditorRemoveFilePendingResponseStepModelRequirementMutation from "@/gql/mutations/AuditorRemoveFilePendingResponseStepModelRequirementMutation.gql";

export default {
  components: {
    ResponseList,
    MarkdownEditor,
  },

  props: {
    type: {
      type: String,
      default: "user",
    },

    model: {
      type: String,
      default: "minimal",
    },

    requirement: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      formData: null,
      valid: false,
      loading: false,
      saved: false,
    };
  },

  computed: {
    mutation() {
      const { type, model } = this;

      if (type === "user" && model === "minimal") return UpdateUserResponseMinimalRequirementMutation;
      if (type === "user" && model === "step") return UpdateUserResponseStepModelRequirementMutation;
      if (type === "auditor" && model === "minimal") return UpdateAuditorResponseMinimalRequirementMutation;
      if (type === "auditor" && model === "step") return UpdateAuditorResponseStepModelRequirementMutation;

      return null;
    },

    removeMutation() {
      const { type, model } = this;

      if (type === "user" && model === "minimal") return RemoveUserPendingResponseMinimalRequirementMutation;
      if (type === "user" && model === "step") return RemoveUserPendingResponseStepModelRequirementMutation;
      if (type === "auditor" && model === "minimal") return RemoveAuditorPendingResponseMinimalRequirementMutation;
      if (type === "auditor" && model === "step") return RemoveAuditorPendingResponseStepModelRequirementMutation;

      return null;
    },

    removeFileMutation() {
      const { type, model } = this;

      if (type === "user" && model === "minimal") return UserRemoveFilePendingResponseMinimalRequirementMutation;
      if (type === "user" && model === "step") return UserRemoveFilePendingResponseStepModelRequirementMutation;
      if (type === "auditor" && model === "minimal") return AuditorRemoveFilePendingResponseMinimalRequirementMutation;
      if (type === "auditor" && model === "step") return AuditorRemoveFilePendingResponseStepModelRequirementMutation;

      return null;
    },
  },

  methods: {
    async save() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      await this.$apollo.mutate({
        mutation: this.mutation,
        variables: {
          id: this.requirement.id,
          input: this.formData,
        },
      });

      this.formData.files = [];
      this.loading = false;
      this.saved = true;
      this.$emit("save");
    },

    async remove() {
      this.loading = true;

      await this.$apollo.mutate({
        mutation: this.removeMutation,
        variables: {
          id: this.requirement.id,
        },
      });

      this.loading = false;
      this.$emit("remove");
    },

    async removeFile(id) {
      await this.$apollo.mutate({
        mutation: this.removeFileMutation,
        variables: {
          id: this.requirement.id,
          attachmentId: id,
        },
      });
    },
  },

  created() {
    this.formData = this.requirement.pendingResponse
      ? {
          text: this.requirement.pendingResponse.text,
        }
      : {
          text: null,
        };
  },
};
</script>
